import React, { Children, PropsWithChildren, useEffect } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { ScrollMenu, VisibilityContext } from '../../vendor/react-horizontal-scrolling-menu';
import { LeftArrow, RightArrow } from './Arrows';
import { useDrag } from './useDrag';
import '../../vendor/react-horizontal-scrolling-menu/styles.css';
import { useSwipe } from './useSwipe';
import { createGlobalStyle } from 'styled-components';
import { useWheel } from './useWheel';
import { AllowableGutter } from '../../fundamentals/spaces/space';

interface IHorizontalScrollGlobalStyleProps {
  allowTouch?: boolean;
  gapInner?: AllowableGutter;
  gapContainer?: AllowableGutter;
  gutterBottom?: AllowableGutter;
}

enum TouchActions {
  NONE = 'none',
  AUTO = 'auto'
}

const HorizontalScrollGlobalStyle = createGlobalStyle<IHorizontalScrollGlobalStyleProps>`
  .react-horizontal-scrolling-menu--inner-wrapper {
    gap: ${({ gapInner, theme }) => (gapInner ? theme.spaces[gapInner] : 0)};
  }

  .react-horizontal-scrolling-menu--scroll-container {
    gap: ${({ gapContainer, theme }) => (gapContainer ? theme.spaces[gapContainer] : 0)};
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    touch-action: ${({ allowTouch }) => (allowTouch ? TouchActions.AUTO : TouchActions.NONE)};
  }
  .react-horizontal-scrolling-menu--item {
    display: flex;
    margin-bottom: ${({ gutterBottom, theme }) => (gutterBottom ? theme.spaces[gutterBottom] : 0)};
  }
`;

const ArrowHiFloatStyle = createGlobalStyle<IHorizontalScrollGlobalStyleProps>`
.horizontal-scroll-styled-arrow {
  justify-content: end;
  height: 20%;
}
`;

type ScrollVisibilityApiType = React.ContextType<typeof VisibilityContext>;

export const HorizontalScroll: React.FC<
  PropsWithChildren<{
    scrollToItem?: number;
    allowTouch?: boolean;
    hiArrows?: boolean;
    noArrows?: boolean;
    autoHideArrow?: boolean;
    gapInner?: AllowableGutter;
    gapContainer?: AllowableGutter;
    gutterBottom?: AllowableGutter;
    showAddTabButton?: boolean;
  }>
> = ({ children, scrollToItem, noArrows, gapInner, gapContainer, allowTouch, hiArrows, autoHideArrow, gutterBottom, showAddTabButton, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'HorizontalScroll');
  const { dragStart, dragStop, dragMove } = useDrag();
  const { onTouchEnd, onTouchMove, onTouchStart } = useSwipe();
  const { onWheel } = useWheel();
  const apiRef = React.useRef({} as ScrollVisibilityApiType);

  const handleDrag =
    ({ scrollContainer }: ScrollVisibilityApiType) =>
    /* istanbul ignore next */
    (ev: React.MouseEvent) =>
      dragMove(ev, (posDiff) => {
        if (scrollContainer.current) {
          scrollContainer.current.scrollLeft += posDiff;
        }
      });

  useEffect(() => {
    if (scrollToItem !== undefined) {
      try {
        apiRef.current?.scrollToItem?.(apiRef.current?.getItemByIndex(scrollToItem));
      } catch {}
    }
  }, [scrollToItem, apiRef.current?.items]);

  return (
    <div onMouseLeave={dragStop}>
      <HorizontalScrollGlobalStyle gapInner={gapInner} gapContainer={gapContainer} gutterBottom={gutterBottom} allowTouch={allowTouch} />
      {hiArrows ? <ArrowHiFloatStyle /> : undefined}
      <ScrollMenu
        LeftArrow={!noArrows && <LeftArrow autoHide={autoHideArrow} />}
        RightArrow={!noArrows && <RightArrow autoHide={autoHideArrow} />}
        onWheel={onWheel}
        onMouseDown={() => dragStart}
        onMouseUp={() => dragStop}
        onMouseMove={handleDrag}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        onTouchStart={onTouchStart}
        apiRef={apiRef}
        {...generateTestId()}
      >
        {Children.map(
          (children as React.ReactElement<{ position?: number; itemId: any }>[]).filter((c) => !!c),
          (tab, position) => React.cloneElement(tab, { position: position + (showAddTabButton ? 0 : 1), itemId: tab.key })
        )}
      </ScrollMenu>
    </div>
  );
};
